import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';

/**
 * FormChangeDetector component
 * Detects changes in a Formik form and calls the onChange callback
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback when form changes
 * @param {Object} props.initialValues - Initial form values
 * @returns {null} This component doesn't render anything
 */
const FormChangeDetector = ({ onChange, initialValues }) => {
  const formik = useFormikContext();
  const [prevValues, setPrevValues] = useState(null);

  // Compare current values with initial values to detect changes
  useEffect(() => {
    if (!prevValues) {
      setPrevValues(formik.values);
      return;
    }

    // Check if any values have changed
    let hasChanged = false;
    const changedFields = [];

    Object.keys(formik.values).forEach(key => {
      // Special handling for file fields
      if (key.includes('File') || key.includes('HasNew')) {
        // If we have a file or hasNew flag is true, consider it changed
        if (
          (key.includes('File') && formik.values[key]) ||
          (key.includes('HasNew') && formik.values[key] === true)
        ) {
          hasChanged = true;
          changedFields.push({
            field: key,
            from: 'previous value',
            to: 'new value'
          });
          return;
        }
      }

      // Check if the value has changed for other fields
      const initialValue = initialValues[key];
      const currentValue = formik.values[key];

      // Convert to strings for comparison to avoid false positives with objects
      const initialValueStr = typeof initialValue === 'object' ? JSON.stringify(initialValue) : String(initialValue || '');
      const currentValueStr = typeof currentValue === 'object' ? JSON.stringify(currentValue) : String(currentValue || '');

      if (initialValueStr !== currentValueStr) {
        hasChanged = true;
        changedFields.push({
          field: key,
          from: initialValue,
          to: currentValue
        });
      }
    });

    onChange(hasChanged, changedFields);
  }, [formik.values, initialValues, onChange, prevValues]);

  return null;
};

export default FormChangeDetector;
